import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { client } from '../../networking/client';
import { RootState } from '../store';
import { isAutoGeneratedEmail } from '../../utils/miscUtils';

interface ICustomrDetails {
  height: number;
  weight: number;
  blood_type: string;
  relation_status: string;
}

export interface ICustomer {
  person_id: number | undefined;
  person_external_id: number;
  external_address_id: number | null;
  person_firstname: string;
  person_lastname: string;
  person_gender_id: number;
  gender?: string;
  person_birthdate: Date | any;
  person_email: string;
  person_mobile_phone: string;
  person_account_number?: string;
  person_details?: ICustomrDetails;
  address_id: number;
  address_type: string;
  street: string;
  house_number: string;
  city: string;
  postcode: number;
  district: string;
  state: string;
  type_id: number;
  country_id: number;
  country_phone_code: string;
  countrycode: string;
  country?: string;
  type: string;
  person_identity_id: number;
  person_identity_type_id: number;
  person_identity_number: string;
  nationality_id: number;
  nationality_name?: string;
  amount_paid?: number;
  vat?: number;
  building_identifier: string;
  language?: string;
  person_datetime_created?: Date | any;
}

export interface ICustomersState {
  status: string;
  addingStatus: string;
  editingStatus: string;
  customers: ICustomer[];
}

const initialState: ICustomersState = {
  status: 'idle',
  addingStatus: 'idle',
  editingStatus: 'idle',
  customers: [],
};

export const fetchCustomers = createAsyncThunk(
  'customers/fetchAll',
  async (_, { dispatch, rejectWithValue, getState }) => {
    const state: any = getState();
    try {
      const customersResponse: any = await client.get(
        `/healthpoint/location_customers/${state?.auth?.locationId}`,

        {
          params: {
            merchantPersonId: state?.auth?.accountInfo?.external_person_id,
          },
        }
      );
      return customersResponse;
    } catch (err: any) {
      return rejectWithValue({});
    }
  }
);

type AddCustomerReqBody = {
  personFirstname: string;
  personLastname: string;
  personGenderId: number | undefined;
  personBirthdate: Date | undefined;
  personEmail?: string;
  personMobilePhone?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: number;
  city?: string;
  countryId?: string;
  personIdentityNumber?: string;
  personNationalityId?: number;
  language: string;
};

export const addCustomer = createAsyncThunk(
  'customer/add',
  async (reqBody: AddCustomerReqBody, { dispatch, rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      await client.post(`/healthpoint/customer/${state?.auth?.basicAuth}`, reqBody);
      dispatch(fetchCustomers());
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    resetCustomers: (_) => {
      return initialState;
    },
    resetCustomerAddingStatus: (state) => {
      return {
        ...state,
        addingStatus: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        const tempoCustomers: ICustomer[] = action.payload.data.customers;
        state.customers = tempoCustomers.map((customer) => {
          return {
            ...customer,
            person_email: isAutoGeneratedEmail(customer.person_email) ? '' : customer.person_email,
          };
        });
        state.status = 'idle';
      })
      .addCase(fetchCustomers.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(addCustomer.pending, (state) => {
        state.addingStatus = 'pending';
      })
      .addCase(addCustomer.fulfilled, (state) => {
        state.addingStatus = 'fulfilled';
      })
      .addCase(addCustomer.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.addingStatus = 'rejected';
      });
  },
});

export const { resetCustomers, resetCustomerAddingStatus } = customersSlice.actions;

export default customersSlice.reducer;

export const selectCustomers = (state: RootState) => state.customers.customers;
export const selectCustomersEditingStatus = (state: RootState) => state.customers.editingStatus;
export const selectCustomerAddingStatus = (state: RootState) => state.customers.addingStatus;
